import { parseUnitValue, transformToNumber } from '../parse';
import { makeCSVImportError } from '../tree';
import { DynamicProperty } from './Property';

/**
 * Shared base for the numeric property types that carry a CSS-style unit
 * suffix - Pixels ('px'), Points ('pt') and Percentage ('%'). Those three were
 * byte-identical apart from the unit string, so a subclass now only has to
 * declare `type` and `unit`.
 *
 * Deliberately not registered in AllTypes: it is a base, not a property type.
 *
 * Not shared with `Number`, which looks similar but is a different kind of
 * value. These three are CSS <length> / <percentage>, so they parse with
 * parseUnitValue, which follows CSS grammar - a unit is required except for
 * zero, hence its null return for any unit-less non-zero quantity. `Number` is
 * a CSS <number>, so it accepts a bare number and nothing else. They also
 * serialise differently: getPreviewData/getCSVData return a number there where
 * these return a string.
 */
export default abstract class UnitNumber extends DynamicProperty<number> {
    value = 0;
    unit = '';

    setDefault = () => {
        this.setInitialValue(0);
    };

    /**
     * The coercion hook. `DynamicGroup.create()` and `DynamicProperty.set()`
     * both route values through transform, which makes this the single place
     * that runs on every assignment.
     *
     * These classes each used to attempt the same coercion in a constructor
     * instead, which could never work: `create()` overwrites `value` on the
     * line after construction, and the subclass's own `value = 0` field
     * initialiser clobbers whatever `super()` assigned. Pixels' version had
     * also grown an `else value = 0` that discarded every number it was given.
     *
     * Accepting strings is legacy-data defence rather than a live path. Values
     * written today leave through getState() as whatever transform produced,
     * and setUsingCSV already calls set() with a real number.
     *
     * Follows the browser's tolerances, since what these values ultimately
     * describe is a browser's rendering. A suffix matching this property's
     * declared unit is not ambiguous, so it is simply read and dropped -
     * '10px' on a Pixels property is 10, with no complaint. A bare number is
     * likewise unambiguous here, precisely because the unit is declared, even
     * though CSS itself only permits a unitless zero.
     *
     * A mismatched unit is the case CSS treats as an invalid declaration and
     * discards, so '10pt' on a Pixels property is not silently read as 10. We
     * cannot literally discard - transform has to return something - so it
     * warns and yields 0.
     *
     * See transformToNumber for why unreadable input becomes 0 rather than
     * undefined.
     */
    transform = (value: any): number => {
        if (typeof value === 'string') {
            const parsed = parseUnitValue(value);
            if (parsed) {
                // A zero carries no unit in CSS terms, whatever it is spelled
                // with, so 0pt is a legitimate 0 for a Pixels property.
                if (parsed.value === 0 || parsed.type === this.unit) {
                    return parsed.value;
                }

                console.warn(
                    `[${this.type}] "${this.name}": expected unit "${this.unit}" but got "${parsed.type}", reading as 0`,
                    value
                );
                return 0;
            }
            // null means a bare non-zero quantity, undefined means not a
            // dimension at all. Both fall through: the first is read as a plain
            // number, the second warns.
        }

        return transformToNumber(value, this);
    };

    getString = () => {
        if (!this.value && this.value !== 0) return '0' + this.unit;
        return this.value + this.unit;
    };

    getPreviewData = () => {
        return this.getString();
    };

    // parseUnitValue distinguishes its two failures, so the errors do too:
    // undefined means the text is not a dimension token at all, null means it
    // parsed but omitted a unit on a non-zero quantity, which CSS only permits
    // for zero. A quantity of zero is accepted whatever unit it carries, or
    // none, for the same reason.
    setUsingCSV = (value: string) => {
        const parsed = parseUnitValue(value);

        if (parsed === undefined) {
            const logValue = JSON.stringify(value);
            throw makeCSVImportError(this, `Not a number. Value: ${logValue}`);
        }

        if (parsed === null) {
            const logValue = JSON.stringify(value);
            const logUnit = JSON.stringify(this.unit);
            throw makeCSVImportError(
                this,
                `Missing unit, expected ${logUnit}. Value: ${logValue}`
            );
        }

        if (parsed.value !== 0 && parsed.type !== this.unit) {
            const logValue = JSON.stringify(value);
            const logUnit = JSON.stringify(this.unit);
            const logGotUnit = JSON.stringify(parsed.type);
            throw makeCSVImportError(
                this,
                `Wrong unit ${logGotUnit}, expected ${logUnit}. Value: ${logValue}`
            );
        }

        this.set(parsed.value);
    };
}
